import type { World } from '../sim/types';
import { plainTitle } from './surveyNames';
import { civilFromEpochDay, dateLabel, epochDayOf } from '../sim/calendar';
import { ISLAND_NAMES, REEF_ISLANDS, type ReefIsland } from '../sim/reef';
import { SURVEY_NAMES, isCleanupHome, protectionJob } from '../sim/operations';
import { shoreDebrisT } from '../sim/shoreDebris';
import { managementOf } from './overview';
import { seeItHtml } from './onTheReef';
import { escapeHtml } from './format';

/**
 * How long a step done stays on its island's card as "Done" (M295, the one-island roadmap's R5): a month, a game choice
 * (M178), the month's report taking it up after.
 */
export const DONE_DAYS = 30;

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'] as const;

/** A survey's next step, its findings (M313: on the done card, the link itself). */
const FINDINGS_NEXT = 'Read its findings: what was counted against the count before.';

/** A step done on an island: its kind, its day, what was done and what it enables next. */
export interface Milestone {
  kind: 'patrol' | 'volunteers' | 'consulted' | 'rule' | 'survey';
  day: number;
  done: string;
  next: string;
}

/**
 * The operational steps done on an island, each from what the operations already hold (no event, no rate, nothing new in
 * the world): a patrol deployed (its day from the command's own result in the HUD, `patrolStarted`, since the world keeps
 * no patrol's first day), its volunteers trained (`community`, the day they count from), its protected area's consultation
 * ended and its rule made (the rule's job and the finished rule's), and a survey home from it (`finished`). Newest first,
 * those within `DONE_DAYS` of today.
 */
export function milestonesOn(world: World, island: ReefIsland, patrolStarted?: number): Milestone[] {
  const ops = world.operations;
  const today = epochDayOf(world);
  const name = ISLAND_NAMES[island];
  const x = managementOf(world)?.islands.find((y) => y.island === island);
  const out: Milestone[] = [];
  const pct = (v: number | undefined): string => `${Math.round(100 * (v ?? 0))}%`;
  if (x && x.patrol.level > 0 && patrolStarted !== undefined) {
    out.push({ kind: 'patrol', day: patrolStarted, done: `Patrol deployed on ${name}: the forbidden catch taken falls from ${pct(x.patrol.takenAnyway[0])} to ${pct(x.patrol.takenAnyway[x.patrol.level])}, from the day it began.`,
      next: `${x.patrol.until !== null ? `Its term ends ${dateLabel(x.patrol.until)}. ` : ''}The fish answer over years, and a count shows it.` });
  }
  const since = ops.community[REEF_ISLANDS.indexOf(island)];
  if (since !== null && since !== undefined) {
    // Counted at a month's end after the day they are trained (`stepOperations`: a month's last day trained counts the next).
    const m = civilFromEpochDay(since + 1);
    out.push({ kind: 'volunteers', day: since, done: `Volunteers trained on ${name}: monthly fish counts begin.`, next: `Their first count at the end of ${MONTHS[m.month - 1]}.` });
  }
  const job = protectionJob(ops, island);
  if (job && today >= (job.phaseEnds[0] as number)) {
    out.push({ kind: 'consulted', day: job.phaseEnds[0] as number, done: `Consultation on ${name} ended: the proposal goes to the State.`, next: `The rule, about ${dateLabel(job.phaseEnds[1] as number)}; nothing changes on the water until then.` });
  }
  const made = ops.finished.filter((f) => f.kind === 'rule' && f.title === `A Herbivore Protection Area on ${name}: in force`).at(-1);
  if (made && x?.herbivoresProtected) {
    out.push({ kind: 'rule', day: made.endDay, done: `A Herbivore Protection Area is in force on ${name}: no parrotfish, kala, nenue or manini may be taken.`, next: 'Patrols keep it. The fish answer over a few years, the coral over many.' });
  }
  const home = ops.finished.filter((f) => f.kind === 'survey' && f.island === island).at(-1);
  if (home) {
    const what = plainTitle(Object.values(SURVEY_NAMES).find((n) => home.title.startsWith(n)) ?? 'A survey');
    // A shore cleanup told as a survey home is (M307), with what it removed and what lies there now.
    out.push(isCleanupHome(home)
      ? { kind: 'survey', day: home.endDay, done: `${what} home from ${name}: ${(home.tonnes ?? 0).toFixed(1)} t of nets and debris removed.`, next: `About ${Math.round(shoreDebrisT(world, island))} t still lies on its shore.` }
      // Its findings read since it came home (`readThreats`'s latest day, M319), its next step the next count.
      : { kind: 'survey', day: home.endDay, done: `${what} home from ${name}.`, next: (world.campaign.marks.readThreats ?? -Infinity) >= home.endDay ? 'Findings read: the next count tells which way it goes.' : FINDINGS_NEXT });
  }
  return out.filter((d) => d.day <= today && today - d.day < DONE_DAYS).sort((a, b) => b.day - a.day);
}

/** A step done as a card's "Done" line (M295's): a survey home its findings' own link, not told again beside it (M313). */
function doneHtml(d: Milestone): string {
  const findings = '<button class="link" data-act="open" data-kind="findings">';
  const next = d.next === FINDINGS_NEXT ? `Next: ${findings}Read its findings</button>` : `Next: ${escapeHtml(d.next)}`;
  // Where it is seen (M332, O7): a patrol's fish and a cleanup's nets in Explore's tally.
  const see = d.kind === 'patrol' ? seeItHtml('patrol') : d.kind === 'survey' && /t of nets/.test(d.done) ? seeItHtml('cleanup') : '';
  return `<div class="done-card" data-kind="${d.kind}"><p><strong>Done:</strong> ${escapeHtml(d.done)}</p><p class="row-note">${next}</p>${d.kind === 'survey' && d.next !== FINDINGS_NEXT ? `${findings}The findings</button>` : ''}${see}</div>`;
}

/**
 * The reef's steps done (M327, one reef: the island card's "Done" line, M295's early accomplishment, kept on the reef's
 * card): the reef's own, its protected area's consultation ended and its rule made for all of Maui Nui, volunteers
 * trained across it, a survey of the whole reef home; then each island's (a stretch patrolled, a shore cleared). Newest
 * first, within `DONE_DAYS`.
 */
function reefMilestones(world: World, patrolStarts: Partial<Record<ReefIsland, number>> = {}): Milestone[] {
  const ops = world.operations;
  const today = epochDayOf(world);
  const out: Milestone[] = [];
  const job = protectionJob(ops, 'all');
  if (job?.rule?.type === 'protect_herbivores' && job.rule.island === 'all' && today >= (job.phaseEnds[0] as number)) {
    out.push({ kind: 'consulted', day: job.phaseEnds[0] as number, done: 'Consultation across Maui Nui ended: the proposal goes to the State.', next: `The rule, about ${dateLabel(job.phaseEnds[1] as number)}; nothing changes on the water until then.` });
  }
  const made = ops.finished.filter((f) => f.kind === 'rule' && f.title === 'A Herbivore Protection Area on all of Maui Nui\'s reef: in force').at(-1);
  if (made && managementOf(world)?.islands.every((x) => x.herbivoresProtected)) {
    out.push({ kind: 'rule', day: made.endDay, done: 'A Herbivore Protection Area is in force across Maui Nui: no parrotfish, kala, nenue or manini may be taken.', next: 'Patrols keep it. The fish answer over a few years, the coral over many.' });
  }
  const trained = ops.community.filter((d): d is number => d !== null);
  const allOn = trained.length === REEF_ISLANDS.length && trained.every((d) => d === trained[0]);
  if (allOn) {
    const m = civilFromEpochDay((trained[0] as number) + 1);
    out.push({ kind: 'volunteers', day: trained[0] as number, done: 'Volunteers trained across Maui Nui: monthly fish counts begin on every island.', next: `Their first count at the end of ${MONTHS[m.month - 1]}.` });
  }
  const home = ops.finished.filter((f) => f.kind === 'survey' && f.island === null && !isCleanupHome(f)).at(-1);
  if (home) {
    const what = plainTitle(Object.values(SURVEY_NAMES).find((n) => home.title.startsWith(n)) ?? 'A survey');
    out.push({ kind: 'survey', day: home.endDay, done: `${what} home from the whole reef.`, next: (world.campaign.marks.readThreats ?? -Infinity) >= home.endDay ? 'Findings read: the next count tells which way it goes.' : FINDINGS_NEXT });
  }
  // Each island's own steps, its volunteers' only where they were not trained across the reef at once.
  // Its consultation and rule only where they were not Maui Nui's own (review 34: the reef-wide job matched each island's
  // too, four copies of the reef's line behind it).
  const reefWide = job?.rule?.type === 'protect_herbivores' && job.rule.island === 'all';
  const reefRule = made !== undefined && (managementOf(world)?.islands.every((x) => x.herbivoresProtected) ?? false);
  for (const island of REEF_ISLANDS) out.push(...milestonesOn(world, island, patrolStarts[island]).filter((d) => !(allOn && d.kind === 'volunteers') && !(reefWide && d.kind === 'consulted') && !(reefRule && d.kind === 'rule')));
  return out.filter((d) => d.day <= today && today - d.day < DONE_DAYS).sort((a, b) => b.day - a.day);
}

/** The reef's newest step done, as its card's "Done" line (M327); none where nothing is within `DONE_DAYS`. */
export function reefDoneHtml(world: World, patrolStarts: Partial<Record<ReefIsland, number>> = {}): string {
  const d = reefMilestones(world, patrolStarts)[0];
  return d ? doneHtml(d) : '';
}
